import React, { useState, useEffect } from "react";
import {
  Modal,
  TextField,
  Box,
  MenuItem,
  Typography,
  List,
  ListItem,
  IconButton,
  Autocomplete,
  Button,
} from "@mui/material";
import MDButton from "components/MDButton";
import { Delete, CheckCircle, Close, Edit, Save } from "@mui/icons-material";
import PropTypes from "prop-types";
import pvmCodes from "common/VATClassifications/pvmCodes";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";

// Function to get CSRF token
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const SupplierEdit = ({ open, onClose, supplier, onUpdate }) => {
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [formData, setFormData] = useState({
    warehouseCode: supplier.primary_warehouse || "",
    groupName: supplier.primary_group || "",
    pvmCode: supplier.pvm_code || "",
    xmlType: supplier.xml_type || "",
  });

  const [supplierInfos, setSupplierInfos] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [companyOptions, setCompanyOptions] = useState({
    combinations: [],
  });
  const { selectedCompany } = useCompany(); // Get selectedCompany from context

  useEffect(() => {
    if (open && selectedCompany) {
      fetchSupplierInfos();
      fetchCompanyOptions();
    }
  }, [open, selectedCompany]);

  const fetchCompanyOptions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/suppliers/options/?company_id=${selectedCompany.id}`,
        { credentials: "include" }
      );
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) {
        // Build unique combinations from all supplier infos in this company
        const combos = (data.combinations || []).map((c, i) => ({
          ...c,
          label: `${c.warehouse_code} / ${c.group_name} / ${c.pvm_code} / ${c.xml_type}`,
          key: i,
        }));
        setCompanyOptions({ combinations: combos });
      }
    } catch (e) {
      // non-critical, ignore
    }
  };

  const fetchSupplierInfos = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/suppliers/info/?supplier_registration=${supplier.supplier_registration}&company_id=${selectedCompany.id}`,
        {
          credentials: "include",
        }
      );
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setSupplierInfos(data.infos.sort((a, b) => b.is_primary - a.is_primary));
      } else {
        console.error("Error fetching supplier infos:", data.error);
      }
    } catch (error) {
      console.error("Error fetching supplier infos:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      const isFirstPosition = supplierInfos.length === 0; // Patikriname, ar nėra jokių pozicijų
      const payload = {
        supplierRegistration: supplier.supplier_registration,
        warehouseCode: formData.warehouseCode,
        groupName: formData.groupName,
        pvmCode: formData.pvmCode,
        xmlType: formData.xmlType,
        isPrimary: isFirstPosition, // Jeigu tai pirma pozicija, automatiškai padarome ją primary
      };

      console.log("Sending the following payload to the API:", payload);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/update_supplier_info/?company_id=${selectedCompany.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCookie("csrftoken"),
          },
          body: JSON.stringify(payload),
          credentials: "include",
        }
      );

      const data = await response.json();
      if (data.success) {
        fetchSupplierInfos(); // Atnaujinti sąrašą
        setFormData({
          warehouseCode: "",
          groupName: "",
          pvmCode: "",
          xmlType: "",
        }); // Išvalyti formos laukus po sėkmingo išsaugojimo
        onUpdate(); // Informuoti tėvinį komponentą apie atnaujinimą
        showSnackbar(t("savedSuccessfully"), "success");
      } else {
        console.error("Server error:", data.error);
        showSnackbar(data.error || t("errorOccurred"), "error");
      }
    } catch (error) {
      console.error("Error updating supplier info:", error);
      showSnackbar(t("errorOccurred"), "error");
    }
  };

  const handleDelete = async (infoId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/remove-position/?company_id=${selectedCompany.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCookie("csrftoken"),
          },
          credentials: "include",
          body: JSON.stringify({ positionId: infoId }),
        }
      );

      const data = await response.json();
      if (data.success) {
        fetchSupplierInfos(); // Atnaujinti sąrašą
      }
    } catch (error) {
      console.error("Error deleting supplier info:", error);
    }
  };

  const handleMakePrimary = async (infoId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/set-selected-position/?company_id=${selectedCompany.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCookie("csrftoken"),
          },
          credentials: "include",
          body: JSON.stringify({
            positionId: infoId,
            supplierRegistration: supplier.supplier_registration,
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        fetchSupplierInfos(); // Atnaujinti sąrašą
        onUpdate(); // Informuoti tėvinį komponentą apie atnaujinimą
        showSnackbar(t("updatedSuccessfully"), "success");
      }
    } catch (error) {
      console.error("Error making supplier info primary:", error);
    }
  };

  const startEditing = (info) => {
    setEditingId(info.id);
    setEditingValues({
      warehouse_code: info.warehouse_code || "",
      group_name: info.group_name || "",
      pvm_code: info.pvm_code || "",
      xml_type: info.xml_type || "",
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingValues({});
  };

  const saveEditing = async () => {
    try {
      const payload = {
        positionId: editingId,
        supplierRegistration: supplier.supplier_registration,
        warehouseCode: editingValues.warehouse_code,
        groupName: editingValues.group_name,
        pvmCode: editingValues.pvm_code,
        xmlType: editingValues.xml_type,
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/update_supplier_info/?company_id=${selectedCompany.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCookie("csrftoken"),
          },
          body: JSON.stringify(payload),
          credentials: "include",
        }
      );
      const data = await response.json();
      if (data.success) {
        showSnackbar(t("savedSuccessfully"), "success");
        setEditingId(null);
        setEditingValues({});
        fetchSupplierInfos();
        onUpdate();
      } else {
        showSnackbar(data.error || t("errorOccurred"), "error");
      }
    } catch (e) {
      console.error(e);
      showSnackbar(t("errorOccurred"), "error");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose} // Modal is closed when 'X' is clicked
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 900, // Larger modal width
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
            {t("editSupplier")}
          </Typography>
          <IconButton edge="end" aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {companyOptions.combinations.length > 0 && (
          <TextField
            select
            label="Kopijuoti iš esamos"
            value=""
            onChange={(e) => {
              const combo = companyOptions.combinations[e.target.value];
              if (combo) {
                setFormData({
                  warehouseCode: combo.warehouse_code,
                  groupName: combo.group_name,
                  pvmCode: combo.pvm_code,
                  xmlType: combo.xml_type,
                });
              }
            }}
            fullWidth
            margin="normal"
            sx={{ mb: 2 }}
          >
            {companyOptions.combinations.map((combo, i) => (
              <MenuItem key={i} value={i}>
                {combo.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          label={t("warehouseCode")}
          value={formData.warehouseCode}
          onChange={(e) => setFormData({ ...formData, warehouseCode: e.target.value })}
          fullWidth
        />
        <TextField
          label={t("groupName")}
          value={formData.groupName}
          onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
          fullWidth
          margin="normal"
        />

        {/* Replace VAT Code TextField with Autocomplete */}
        <Autocomplete
          options={pvmCodes}
          getOptionLabel={(option) => option.label}
          filterOptions={(options, { inputValue }) =>
            options.filter(
              (option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.value.toLowerCase().includes(inputValue.toLowerCase())
            )
          }
          renderInput={(params) => (
            <TextField {...params} label={t("pvmCode")} margin="normal" fullWidth />
          )}
          value={pvmCodes.find((code) => code.value === formData.pvmCode) || null}
          onChange={(event, newValue) => {
            setFormData({ ...formData, pvmCode: newValue ? newValue.value : "" });
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
        />

        <TextField
          label={t("xmlType")}
          select
          value={formData.xmlType}
          onChange={(e) => setFormData({ ...formData, xmlType: e.target.value })}
          fullWidth
          margin="normal"
        >
          <MenuItem value="quantitative">Quantitative</MenuItem>
          <MenuItem value="summary">Summary</MenuItem>
        </TextField>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            {t("save")}
          </Button>
        </Box>

        <Typography variant="h6" mt={4} gutterBottom>
          {t("savedVariants")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid black",
            paddingBottom: "8px",
            mb: 2,
          }}
        >
          <Typography variant="body1" sx={{ width: "20%" }}>
            <strong>{t("warehouseCode")}</strong>
          </Typography>
          <Typography variant="body1" sx={{ width: "20%" }}>
            <strong>{t("groupName")}</strong>
          </Typography>
          <Typography variant="body1" sx={{ width: "15%" }}>
            <strong>{t("vatCode")}</strong>
          </Typography>
          <Typography variant="body1" sx={{ width: "15%" }}>
            <strong>{t("xmlType")}</strong>
          </Typography>
          <Typography variant="body1" sx={{ width: "15%" }}>
            <strong>{t("deletePrimary")}</strong>
          </Typography>
        </Box>

        <List>
          {supplierInfos.map((info) => (
            <ListItem
              key={info.id}
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              {/* Warehouse Code */}
              <Box sx={{ width: "20%" }}>
                {editingId === info.id ? (
                  <TextField
                    value={editingValues.warehouse_code}
                    onChange={(e) =>
                      setEditingValues({ ...editingValues, warehouse_code: e.target.value })
                    }
                    size="small"
                    fullWidth
                  />
                ) : (
                  <Typography>{info.warehouse_code}</Typography>
                )}
              </Box>
              {/* Group Name */}
              <Box sx={{ width: "20%" }}>
                {editingId === info.id ? (
                  <TextField
                    value={editingValues.group_name}
                    onChange={(e) =>
                      setEditingValues({ ...editingValues, group_name: e.target.value })
                    }
                    size="small"
                    fullWidth
                  />
                ) : (
                  <Typography>{info.group_name}</Typography>
                )}
              </Box>
              {/* PVM Code */}
              <Box sx={{ width: "15%" }}>
                {editingId === info.id ? (
                  <Autocomplete
                    options={pvmCodes}
                    getOptionLabel={(option) => option.label}
                    value={pvmCodes.find((c) => c.value === editingValues.pvm_code) || null}
                    onChange={(e, newVal) =>
                      setEditingValues({ ...editingValues, pvm_code: newVal ? newVal.value : "" })
                    }
                    renderInput={(params) => <TextField {...params} size="small" />}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                ) : (
                  <Typography>{info.pvm_code}</Typography>
                )}
              </Box>
              {/* XML Type */}
              <Box sx={{ width: "15%" }}>
                {editingId === info.id ? (
                  <TextField
                    select
                    value={editingValues.xml_type}
                    onChange={(e) =>
                      setEditingValues({ ...editingValues, xml_type: e.target.value })
                    }
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="quantitative">Quantitative</MenuItem>
                    <MenuItem value="summary">Summary</MenuItem>
                  </TextField>
                ) : (
                  <Typography>{info.xml_type}</Typography>
                )}
              </Box>
              {/* Actions */}
              <Box sx={{ width: "15%", display: "flex", alignItems: "center" }}>
                {editingId === info.id ? (
                  <>
                    <IconButton aria-label="save" onClick={saveEditing}>
                      <Save />
                    </IconButton>
                    <IconButton aria-label="cancel" onClick={cancelEditing}>
                      <Close />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton aria-label="edit" onClick={() => startEditing(info)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDelete(info.id)}
                    >
                      <Delete />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="make primary"
                      onClick={() => handleMakePrimary(info.id)}
                      color={info.is_primary ? "success" : "default"}
                    >
                      <CheckCircle />
                    </IconButton>
                  </>
                )}
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>
    </Modal>
  );
};

// Prop validation using prop-types
SupplierEdit.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  supplier: PropTypes.shape({
    supplier_registration: PropTypes.string.isRequired,
    primary_warehouse: PropTypes.string,
    primary_group: PropTypes.string,
    pvm_code: PropTypes.string,
    xml_type: PropTypes.string,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default SupplierEdit;
